import { useId } from 'react'

export interface SegmentedOption<T extends string> {
  value: T
  label: string
}

interface SegmentedProps<T extends string> {
  options: ReadonlyArray<SegmentedOption<T>>
  value: T
  onChange: (value: T) => void
  ariaLabel?: string
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
}: SegmentedProps<T>): JSX.Element {
  const groupId = useId()
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel ?? 'segmented control'}
      id={groupId}
      style={{
        display: 'inline-flex',
        padding: 3,
        background: 'var(--surface-2)',
        border: '1px solid var(--border)',
        borderRadius: 10,
        gap: 2,
      }}
    >
      {options.map((option) => {
        const selected = option.value === value
        return (
          <button
            type="button"
            key={option.value}
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
                event.preventDefault()
                const idx = options.findIndex((o) => o.value === value)
                const next = options[(idx + 1) % options.length]
                if (next) onChange(next.value)
              }
              if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
                event.preventDefault()
                const idx = options.findIndex((o) => o.value === value)
                const next = options[(idx - 1 + options.length) % options.length]
                if (next) onChange(next.value)
              }
            }}
            style={{
              padding: '6px 14px',
              fontSize: 13,
              fontWeight: 500,
              borderRadius: 7,
              background: selected ? 'var(--surface)' : 'transparent',
              color: selected ? 'var(--text)' : 'var(--text-2)',
              boxShadow: selected ? '0 1px 2px rgba(0,0,0,0.04)' : 'none',
              transition:
                'background var(--motion-duration-base) var(--motion-ease-out),' +
                ' color var(--motion-duration-base) var(--motion-ease-out)',
            }}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}
